import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CreditCard, ShieldCheck, AlertTriangle, ArrowRight } from 'lucide-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmailPromptModal from '../../components/EmailPromptModal';
import { useAuth } from '../../hooks/useAuth';
import * as pkgApi from '../../api/packages.api';
import * as ordersApi from '../../api/orders.api';
import * as paymentsApi from '../../api/payments.api';
import * as quizApi from '../../api/quiz.api';
import { errorMessage } from '../../api/client';
import { trackBeginCheckout } from '../../utils/analytics';

// Landing-side sessionStorage key where the intake payload is stashed
// after arrival from `/register?intake=…` — kept in sync with Register.js.
const INTAKE_STORAGE_KEY = 'mdai_pending_intake_v1';

// Decode a URL-safe base64 payload into an object. Silent on any error so
// a tampered/garbled param becomes a no-op.
function decodeIntake(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const padded = raw.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 ? padded + '='.repeat(4 - (padded.length % 4)) : padded;
    const json = decodeURIComponent(escape(window.atob(pad)));
    const obj = JSON.parse(json);
    return obj && typeof obj === 'object' ? obj : null;
  } catch {
    return null;
  }
}

// Picks the "default" package for the one-click Pay & Book flow.
// Preference order (highest → lowest):
//   1. Exact slug match on DEFAULT_PACKAGE_SLUG (the seeded consultation fee).
//   2. Name containing "consultation" (specific, unambiguous).
//   3. Cheapest active package (safe fallback, never picks a premium plan).
// NB: don't use generic keywords like "standard" / "weight" — they'd match
// the existing GLP-1 tier names (GLP-1 Standard 2-Bottle at MYR 3,400, etc.)
// and PayNow would send the customer to the wrong price.
const DEFAULT_PACKAGE_SLUG = 'consultation-programme';
function pickDefaultPackage(all) {
  if (!Array.isArray(all) || all.length === 0) return null;
  const active = all.filter((p) => p?.isActive !== false);
  const pool = active.length ? active : all;
  const bySlug = pool.find((p) => p.slug === DEFAULT_PACKAGE_SLUG);
  if (bySlug) return bySlug;
  const byName = pool.find((p) => (p.name || '').toLowerCase().includes('consultation'));
  if (byName) return byName;
  // Fall back to the cheapest active package — never assume a premium
  // tier for the one-click flow.
  return [...pool].sort((a, b) => (a.price || 0) - (b.price || 0))[0];
}

// PayNow — a "just take me to the payment page" landing target used by the
// intake "Pay & Book My Consultation" CTA on the marketing site. On mount:
//   1. If `?intake=<base64>` is present, submit it to the quiz endpoint so
//      the eligibility record is on file before the order is created.
//   2. Pick the default programme package.
//   3. Create an Order for that package.
//   4. If the customer has no email on file, prompt for one — Curlec's
//      Payment Links API requires customer.email.
//   5. Create a Curlec payment link for the Order.
//   6. Redirect the browser to the hosted checkout URL.
// Guarded by AppLayout, so unauthenticated visitors hit /login?from=/pay-now
// first (with any `?intake=` param preserved through the round-trip).
function PayNow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [status, setStatus] = useState('starting'); // 'starting' | 'preparing' | 'awaiting-email' | 'redirecting' | 'error'
  const [message, setMessage] = useState('Preparing your payment…');
  const [err, setErr] = useState('');
  const [pendingOrder, setPendingOrder] = useState(null);
  const [pendingPkg, setPendingPkg] = useState(null);
  const startedRef = useRef(false); // prevent double-run under React StrictMode

  // Extracted so we can call it either directly (user already has email)
  // or after the email-prompt modal succeeds.
  const createPaymentAndRedirect = useCallback(async (order, pkg, customerEmail) => {
    setStatus('preparing');
    setMessage('Contacting Curlec…');
    // GA4 conversion event — customer has reached the RM1,599 checkout.
    trackBeginCheckout({
      value: pkg?.price || 1599.00,
      currency: pkg?.currency || 'MYR',
      order_id: order._id,
      package_code: pkg?.code || pkg?.name,
    });
    const pay = await paymentsApi.createPayment(order._id, customerEmail);
    if (!pay?.paymentUrl) {
      throw new Error('Payment could not be started. Please try again.');
    }
    setStatus('redirecting');
    setMessage('Redirecting to secure payment…');
    window.location.replace(pay.paymentUrl);
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        // ── 1. Intake payload — either from URL or from sessionStorage
        //    (Register.js stashes it there mid-signup). Best-effort; a
        //    failing quiz submit does NOT block payment.
        setStatus('preparing');
        setMessage('Saving your assessment…');
        const intakeRaw = params.get('intake');
        let intakePayload = intakeRaw ? decodeIntake(intakeRaw) : null;
        if (!intakePayload) {
          try {
            const cached = sessionStorage.getItem(INTAKE_STORAGE_KEY);
            if (cached) intakePayload = JSON.parse(cached);
          } catch { /* ignore */ }
        }
        if (intakePayload) {
          try {
            await quizApi.submitQuiz(intakePayload);
            try { sessionStorage.removeItem(INTAKE_STORAGE_KEY); } catch { /* ignore */ }
          } catch { /* non-fatal */ }
        }

        // ── 2. Pick the default package.
        setMessage('Choosing your programme…');
        const all = await pkgApi.listPackages({ active: 'true' }).catch(() => []);
        const pkg = pickDefaultPackage(all || []);
        if (!pkg?._id) {
          throw new Error('No active programme package found. Please contact support.');
        }

        // ── 3. Create the order.
        setMessage('Creating your booking…');
        const order = await ordersApi.createOrder(pkg._id);
        if (!order?._id) throw new Error('Could not create your order.');
        if (cancelled) return;

        // ── 4. Email gate — pause the flow if the customer has no email.
        //    Curlec requires customer.email even when email notifications
        //    are opted out. We capture it here + save to profile.
        if (!user?.email) {
          setPendingOrder(order);
          setPendingPkg(pkg);
          setStatus('awaiting-email');
          setMessage('One more step…');
          return;
        }

        // ── 5+6. Create payment link + redirect.
        await createPaymentAndRedirect(order, pkg);
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setErr(errorMessage(e));
      }
    })();

    return () => { cancelled = true; };
  }, [params, user?.email, createPaymentAndRedirect]);

  async function onEmailSaved(email) {
    // Client 2026-09-08: email is per-payment only, NOT saved to profile.
    // Pass it straight to createPayment so Curlec sees it.
    try {
      await createPaymentAndRedirect(pendingOrder, pendingPkg, email);
    } catch (e) {
      setStatus('error');
      setErr(errorMessage(e));
    }
  }

  function onEmailCancel() {
    // Customer declined — bounce them to their order details so they can
    // retry (or edit their profile) whenever they're ready.
    toast('You can add an email later from Profile.');
    if (pendingOrder?._id) navigate(`/orders/${pendingOrder._id}`, { replace: true });
    else navigate('/dashboard', { replace: true });
  }

  function retry() {
    startedRef.current = false;
    setStatus('starting');
    setErr('');
    // Bounce through the same URL to re-trigger the effect cleanly.
    navigate(0);
  }

  if (status === 'error') {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
        <div className="card" style={{ padding: 24 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', background: 'var(--rose-50, #fef1f2)', color: 'var(--rose-600, #e11d48)', marginBottom: 16 }}>
            <AlertTriangle size={26} />
          </div>
          <h2 style={{ margin: 0 }}>Payment could not be started</h2>
          <p className="muted" style={{ marginTop: 12 }}>{err}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
            <button className="btn btn-primary" onClick={retry}>
              <CreditCard size={16} /> Try again
            </button>
            <button className="btn btn-ghost" onClick={() => navigate('/packages')}>
              Browse plans <ArrowRight size={16} />
            </button>
          </div>
          <p className="text-xs muted" style={{ marginTop: 16 }}>
            If this keeps happening, please contact support — no charge has been made.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ maxWidth: 480, margin: '80px auto', padding: '0 16px', textAlign: 'center' }}>
        <div className="card" style={{ padding: 32 }}>
          <LoadingSpinner size="lg" />
          <h2 style={{ marginTop: 20 }}>{message}</h2>
          <p className="muted text-sm" style={{ marginTop: 8 }}>
            {status === 'awaiting-email'
              ? 'Please enter your email in the prompt to continue.'
              : "You'll be redirected to Curlec's secure payment page. Please don't close this window."}
          </p>
          <div className="row" style={{ justifyContent: 'center', gap: 8, marginTop: 20, color: 'var(--text-muted)', fontSize: 12 }}>
            <ShieldCheck size={14} />
            <span>Secure checkout · FPX, cards, e-wallets via Curlec</span>
          </div>
        </div>
      </div>

      <EmailPromptModal
        open={status === 'awaiting-email'}
        onSuccess={onEmailSaved}
        onCancel={onEmailCancel}
      />
    </>
  );
}

export default PayNow;
